import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Command, AlertTriangle, Globe, Radio, Database, Shield, FileWarning, Key, ChevronRight } from 'lucide-react';
import { searchData } from '../../data/search';
import { Link } from 'react-router-dom';

interface CommandMenuProps {
  placeholder?: string;
  className?: string;
}

export const CommandMenu: React.FC<CommandMenuProps> = ({ 
  placeholder = "Search assets, findings, or type / for commands...",
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filteredResults = query === '' ? [] : searchData.filter((item) => {
    const searchTerm = query.toLowerCase();
    return (
      item.title.toLowerCase().includes(searchTerm) ||
      item.description.toLowerCase().includes(searchTerm) ||
      item.tags.some(tag => tag.toLowerCase().includes(searchTerm))
    );
  });

  const getItemIcon = (type: string) => {
    switch (type) {
      case 'vulnerability': return AlertTriangle;
      case 'domain': return Globe;
      case 'port': return Radio;
      case 'asset': return Database;
      case 'finding': return Shield;
      case 'data_leak': return FileWarning;
      case 'credential_leak': return Key;
      default: return Database;
    }
  };

  const getItemColor = (type: string) => {
    switch (type) {
      case 'vulnerability': return 'text-red-500 bg-red-500/10';
      case 'domain': return 'text-blue-500 bg-blue-500/10';
      case 'port': return 'text-purple-500 bg-purple-500/10';
      case 'asset': return 'text-emerald-500 bg-emerald-500/10';
      case 'finding': return 'text-yellow-500 bg-yellow-500/10';
      case 'data_leak': return 'text-orange-500 bg-orange-500/10';
      case 'credential_leak': return 'text-indigo-500 bg-indigo-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative group">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-5 w-5 group-hover:text-gray-400 transition-colors" />
        <input
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsOpen(true)}
          className="w-full pl-10 pr-12 py-2 rounded-lg bg-[#1A1F2E] border border-gray-800/50 text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
          disabled
        />
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <kbd className="hidden sm:inline-flex items-center px-2 py-1 text-xs text-gray-500 bg-gray-800/50 rounded border border-gray-700/50">
            ⌘K
          </kbd>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setIsOpen(false)}
            />

            {/* Dropdown */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="absolute top-full mt-2 w-full bg-[#1A1F2E] rounded-lg border border-gray-800 shadow-xl z-50"
            >
              {/* Search Header */}
              <div className="p-2 border-b border-gray-800">
                <div className="flex items-center space-x-2 px-3 py-1.5 text-sm text-gray-400">
                  <Command className="h-4 w-4" />
                  <span>Type a command or search...</span>
                </div>
              </div>

              {/* Search Results */}
              <div className="max-h-[60vh] overflow-y-auto">
                {filteredResults.length > 0 ? (
                  <div className="p-2">
                    {filteredResults.map((item) => {
                      const ItemIcon = getItemIcon(item.type);
                      const colorClass = getItemColor(item.type);
                      
                      return (
                        <Link
                          key={item.id}
                          to={item.link}
                          onClick={() => setIsOpen(false)}
                          className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-800/50 transition-colors group/item"
                        >
                          <div className={`p-2 rounded-lg ${colorClass}`}>
                            <ItemIcon className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <div className="text-sm font-medium text-white">{item.title}</div>
                              <ChevronRight className="h-4 w-4 text-gray-500 opacity-0 group-hover/item:opacity-100 transition-opacity" />
                            </div>
                            <div className="text-xs text-gray-400 mt-0.5">{item.description}</div>
                            {item.tags.length > 0 && (
                              <div className="flex items-center space-x-2 mt-2">
                                {item.tags.map((tag, index) => (
                                  <span
                                    key={index}
                                    className="px-1.5 py-0.5 text-xs rounded-full bg-gray-800/50 text-gray-400"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                ) : query !== '' ? (
                  <div className="p-8 text-center">
                    <Search className="h-8 w-8 text-gray-500 mx-auto mb-3" />
                    <p className="text-sm text-gray-400">No results found for "{query}"</p>
                  </div>
                ) : (
                  <div className="p-2 space-y-1">
                    {[
                      { icon: Shield, label: 'View security findings', shortcut: '⌘F' },
                      { icon: Database, label: 'Browse assets', shortcut: '⌘A' },
                      { icon: Globe, label: 'Domain overview', shortcut: '⌘D' },
                      { icon: FileWarning, label: 'Check data leaks', shortcut: '⌘L' }
                    ].map((item, index) => (
                      <button
                        key={index}
                        className="w-full flex items-center justify-between px-3 py-1.5 rounded hover:bg-gray-800/50 text-sm text-gray-400 hover:text-white transition-colors"
                      >
                        <div className="flex items-center space-x-2">
                          <item.icon className="h-4 w-4" />
                          <span>{item.label}</span>
                        </div>
                        <kbd className="px-1.5 py-0.5 text-xs bg-gray-800/50 rounded border border-gray-700/50">
                          {item.shortcut}
                        </kbd>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};